import { Commodity } from '@enmon/commons/lib/shared';
import { isTariff, TARIFFS } from '@enmon/commons/lib/shared/meter/registers/tariff';
import { isUnit, type Unit } from '@enmon/commons/lib/shared/unit';
import { translateInventoryText } from '@/shared/i18n/inventory-i18n';
import {
  formatEnumKey,
  formatUnitLabel,
  getAllowedUnitsForCommodity,
  getObisMapForCommodity,
} from '@/shared/meters/register-metadata-options';

export interface RegisterOption {
  label: string;
  value: string;
}

export function getObisOptionsForCommodity(commodity: Commodity | ''): RegisterOption[] {
  const obisMap = getObisMapForCommodity(commodity);
  const namespace = getObisTranslationNamespace(commodity);

  return Object.entries(obisMap)
    .map(([key, value]) => ({
      label: `${value} - ${
        namespace
          ? translateInventoryText(`app.common.meter.register.${namespace}.${key}`, formatEnumKey(key))
          : formatEnumKey(key)
      }`,
      value,
    }))
    .toSorted((left, right) => left.value.localeCompare(right.value, undefined, { numeric: true }));
}

function getObisTranslationNamespace(commodity: Commodity | '') {
  switch (commodity) {
    case Commodity.Electricity:
      return 'Electricity';
    case Commodity.NaturalGas:
      return 'Gas';
    case Commodity.Other:
      return 'Other';
    case Commodity.Thermal:
      return 'HeatCold';
    case Commodity.Water:
      return 'Water';
    default:
      return '';
  }
}

export function getTariffOptions(): RegisterOption[] {
  return TARIFFS.map(tariff => ({
    label: String(tariff),
    value: String(tariff),
  }));
}

export function getUnitOptionsForCommodity(commodity: Commodity | '', obis: string | undefined): RegisterOption[] {
  const allowedUnits = getAllowedUnitsForCommodity(commodity);
  const normalizedObis = readRegisterObis(obis);
  const units =
    normalizedObis && allowedUnits[normalizedObis] ? allowedUnits[normalizedObis] : Object.values(allowedUnits).flat();

  return [...new Set(units)].map(unit => ({
    label: formatUnitLabel(unit),
    value: unit,
  }));
}

export function readRegisterObis(value: unknown) {
  const text = readString(value).trim();

  if (!text) {
    return undefined;
  }

  return text;
}

export function readRegisterTariff(value: unknown) {
  const text = readString(value).trim();

  if (!text) {
    return undefined;
  }

  const numericValue = Number(text);

  return isTariff(numericValue) ? numericValue : undefined;
}

export function readRegisterUnit(value: unknown): Unit | undefined {
  const text = readString(value).trim();

  return isUnit(text) ? text : undefined;
}

function readString(value: unknown) {
  return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
}
